// Compact, read-only outline of a workout plan tree.
// Used on the pre-session ready screen, the active-workout pause overlay,
// and the standalone-timer (Quick Session) pause overlay.

import React, { useRef, useEffect, useMemo } from "react";
import { Pencil, Repeat } from "lucide-react";
import { cn } from "lib/utils";
import {
  getDotClass,
  getBadgeClass,
  formatExVal,
  formatNodeVal,
  buildSetSchemeSegments,
  resolveSetScheme,
  getActiveTokens,
  isTransitionLabel,
  isHiddenInSummary,
} from "../../utils/planVisuals";
import { formatLabel } from "../../utils/formatLabel";
import { formatDurationCompact } from "../../utils/time";
import useTotalDuration, {
  useGroupRoundSummary,
} from "../../hooks/useTotalDuration";
import { TIMER_PRESETS, getTimerMacroLabels } from "../../config/timers";
import { getStandalonePhaseNodes } from "../../utils/devicePlan";
import {
  buildPhaseStatsMap,
  formatPhaseSummary,
  formatPhaseDuration,
} from "../../utils/phaseLog";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compare two lodash-style paths for equality. */
const pathsEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const pathKey = (path) => path.join(",");

const snapshotsEqual = (a, b) => {
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

/**
 * Build a flat visit-order of every leaf instance produced by a full expansion
 * of the plan tree. Each entry is { path, snapshot } where `snapshot` records
 * the loopCounter value of every repeating ancestor at that instance — this
 * matches the snapshot shape emitted by workoutCompiler.walk, so the runtime's
 * current `loopCounters` can disambiguate which iteration of a repeated path
 * is active.
 */
const buildVisitOrder = (plan) => {
  const order = [];
  const snapshot = {};
  const walk = (node, path) => {
    if (!node) return;
    if (!node.children || node.children.length === 0) {
      order.push({ path, snapshot: { ...snapshot } });
      return;
    }
    const isGroup = node.type === "Group";
    const total = isGroup && node.value > 0 ? node.value : 1;
    for (let iter = 0; iter < total; iter++) {
      if (isGroup && node.id != null) snapshot[node.id] = iter;
      node.children.forEach((child, idx) => {
        walk(child, [...path, "children", idx]);
      });
    }
    if (isGroup && node.id != null) delete snapshot[node.id];
  };
  if (plan?.children) {
    plan.children.forEach((child, idx) => {
      walk(child, ["children", idx]);
    });
  }
  return order;
};

/**
 * Find the current queue position within a visit order. Matches both path
 * and loopCounter snapshot so we land on the *correct iteration* of a
 * repeated leaf, not just its first occurrence.
 */
const findCurrentIdx = (order, currentPath, loopCounters) => {
  if (!currentPath || order.length === 0) return -1;
  let firstPathMatch = -1;
  for (let i = 0; i < order.length; i++) {
    if (!pathsEqual(order[i].path, currentPath)) continue;
    if (firstPathMatch === -1) firstPathMatch = i;
    if (snapshotsEqual(order[i].snapshot, loopCounters || {})) return i;
  }
  return firstPathMatch;
};

/**
 * Group the visit order by leaf path so we can, for any node in the render
 * tree, count total instances and how many have already been traversed.
 */
const buildInstancesByPath = (order) => {
  const map = new Map();
  order.forEach((entry, idx) => {
    const key = pathKey(entry.path);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
    }
    bucket.push(idx);
  });
  return map;
};

/**
 * Return progress info for a leaf path.
 *   status:      "completed" | "current" | "upcoming" | "planned"
 *                ("planned" = session not started, so nothing is dimmed)
 *   completions: number of full iterations already traversed
 *   total:       total number of iterations scheduled across the whole plan
 * A leaf is only "completed" (line-through) once every scheduled iteration
 * has been visited — repeating leaves stay live until their final round.
 */
const getNodeStatus = (nodePath, currentIdx, instancesByPath) => {
  const instances = instancesByPath.get(pathKey(nodePath)) || [];
  const total = instances.length;
  if (total === 0) return { status: "upcoming", completions: 0, total: 0 };
  if (currentIdx === -1) {
    return { status: "planned", completions: 0, total };
  }
  let completions = 0;
  let isCurrent = false;
  for (const idx of instances) {
    if (idx < currentIdx) completions += 1;
    else if (idx === currentIdx) isCurrent = true;
  }
  if (isCurrent) return { status: "current", completions, total };
  if (completions === total) return { status: "completed", completions, total };
  return { status: "upcoming", completions, total };
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Small round-completion indicator shown on repeating leaves.
 * Uses filled dots (same colour family as the leaf's type) up to 6 rounds,
 * and falls back to a "done/total" numeric label beyond that.
 */
const RoundsIndicator = ({ nodeType, completions, total }) => {
  if (total <= 1) return null;
  const dotColor = getDotClass(nodeType);
  
  // Keep the dots for small, micro-repeats (<= 6) 
  if (total <= 6) {
    return (
      <span
        className="flex items-center gap-0.5"
        aria-label={`${completions} of ${total} rounds complete`}
      >
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={cn(
              "w-1.5 h-1.5 rounded-full",
              i < completions ? dotColor : "border border-white/25",
            )}
          />
        ))}
      </span>
    );
  }
  
  // OMIT the text fallback entirely for large repeats. 
  // The parent GroupNode handles the progress context (e.g., Tabata 1/8).
  return null; 
};

/**
 * The per-set ramp of a repeating leaf, e.g. "8/7/6/5 reps · 60/65/70/70 kg".
 * `activeSetIdx` lights the set currently running, so the line doubles as a
 * position indicator through the block (issue #42). Renders nothing when the
 * node carries no scheme.
 */
const SetSchemeLine = ({ node, activeSetIdx, className }) => {
  const segments = buildSetSchemeSegments(node);
  if (segments.length === 0) return null;
  return (
    <div className={className}>
      {segments.map((segment, segmentIdx) => (
        <React.Fragment key={segment.dim}>
          {segmentIdx > 0 && <span className="opacity-50">{" · "}</span>}
          {segment.values.map((entry, setIdx) => (
            <React.Fragment key={setIdx}>
              {setIdx > 0 && <span className="opacity-50">/</span>}
              {/* Sets other than the live one recede, so the ramp reads as a
                  position indicator while the block runs. With no live set
                  (idle, or no enclosing counter) every entry stays full
                  strength — the whole ramp is the prescription. */}
              <span
                className={cn(
                  activeSetIdx != null &&
                    setIdx !== activeSetIdx &&
                    "opacity-40",
                )}
              >
                {entry}
              </span>
            </React.Fragment>
          ))}
          {segment.suffix ? ` ${segment.suffix}` : ""}
        </React.Fragment>
      ))}
    </div>
  );
};

/** A single leaf node row (Work, Rest, SetRest, Warmup, Cooldown). */
const LeafRow = React.forwardRef(
  (
    {
      node,
      status,
      completions,
      total,
      actualStats,
      currentPhaseElapsedSec,
      activeSetIdx,
    },
    ref,
  ) => {
    const typeKey = (node.type || "").toLowerCase().split(" ")[0];
    const isTransition =
      typeKey === "transition" ||
      ((typeKey === "rest" || typeKey === "setrest") && isTransitionLabel(node.label));
    const isRest =
      typeKey === "rest" || typeKey === "setrest" || typeKey === "transition";
    const label = formatLabel(node.label) || (isRest ? "Rest" : "Exercise");
    const actualSummary = formatPhaseSummary(actualStats);

    // Status flags
    const isFullyDone = status === "completed";
    const isCurrent = status === "current";
    const isUpcoming = status === "upcoming";
    const isRepeating = total > 1;
    // Before the session starts the outline is the thing you read to know
    // what's coming, so it gets full contrast and larger detail text.
    const isPlanned = status === "planned";

    // Per-set variation (issue #42). The node's static value only mirrors set 1,
    // so a ramped leaf must not display it as though it held for every set:
    //   • running  → the set actually in progress, resolved the same way the
    //     engine resolves it, with that entry lit in the ramp below.
    //   • otherwise → nothing here at all; the ramp IS the prescription, and a
    //     lone "8 reps" beside "8/7/6/5 reps" is the bug this fixes.
    const hasScheme = buildSetSchemeSegments(node).length > 0;
    const liveSetIdx =
      isCurrent && Number.isFinite(activeSetIdx) ? activeSetIdx : null;
    const displayNode =
      liveSetIdx != null ? resolveSetScheme(node, liveSetIdx) : node;
    const displayValue =
      hasScheme && liveSetIdx == null ? "" : formatNodeVal(displayNode);

    // Fetch dynamic colours using the node's structural type + label
    const activeTokens = getActiveTokens(node.type, node.label);

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col px-2 rounded text-sm transition-all duration-300",
          isPlanned ? "py-1.5" : "py-1",
          isCurrent && cn(activeTokens.bg, "border-l-2 pl-1.5", activeTokens.border),
          isUpcoming && "opacity-50",
          isFullyDone && "opacity-30"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                "w-2 h-2 rounded-full shrink-0 transition-opacity",
                getDotClass(node.type, node.label),
                isCurrent ? "opacity-100" : "opacity-60"
              )}
            />
            <span
              className={cn(
                "text-[10px] font-bold uppercase px-1 py-0.5 rounded leading-none transition-colors",
                getBadgeClass(node.type, node.label),
                isCurrent ? "" : "opacity-80"
              )}
            >
              {isTransition
                ? node.label
                : typeKey === "setrest"
                  ? "Set Rest"
                  : node.type?.split(" ")[0] || ""}
            </span>
            <span
              className={cn(
                "truncate font-medium",
                isCurrent ? activeTokens.text : "text-white/80",
                isFullyDone && "text-white/50"
              )}
            >
              {label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 whitespace-nowrap ml-2">
            {/* Omit dots completely if it is a Rest/SetRest phase */}
            {isRepeating && !isRest && (
              <RoundsIndicator
                nodeType={node.type}
                completions={completions}
                total={total}
              />
            )}
            {displayValue && (
              <span className={cn(
                "text-xs transition-colors",
                isCurrent
                  ? activeTokens.time
                  : isPlanned
                    ? "text-white/75 tabular-nums"
                    : "text-white/50"
              )}>
                {displayValue}
              </span>
            )}
            {isFullyDone && actualSummary && (
              <span className="text-[10px] text-white/30 tabular-nums">
                [{actualSummary}]
              </span>
            )}
            {isCurrent &&
              Number.isFinite(currentPhaseElapsedSec) &&
              currentPhaseElapsedSec > 0 && (
                <span
                  className={cn(
                    "text-[10px] tabular-nums opacity-80",
                    activeTokens.time,
                  )}
                >
                  {formatPhaseDuration(currentPhaseElapsedSec)}…
                </span>
              )}
          </div>
        </div>

        <SetSchemeLine
          node={node}
          activeSetIdx={liveSetIdx}
          className={cn(
            "pl-6 mt-0.5 font-semibold tabular-nums tracking-wide",
            isPlanned ? "text-xs" : "text-[10px]",
            isCurrent ? activeTokens.time : isPlanned ? "text-white/75" : "text-white/60"
          )}
        />

        {node.linkToPrevious && (
          <div className={cn("pl-6 mt-0.5", isPlanned ? "text-xs" : "text-[10px]", "text-muted-foreground")}>
            {"\u21b3 Linked to previous: one Done for both"}
          </div>
        )}
        {node.intensityTarget && (
          <div className={cn(
             "pl-6 mt-0.5 font-medium tracking-wide",
             isPlanned ? "text-xs" : "text-[10px]",
             isCurrent ? activeTokens.time : isPlanned ? "text-primary/80" : "text-primary/50"
          )}>
            Target: {node.intensityTarget}
          </div>
        )}
      </div>
    );
  },
);
LeafRow.displayName = "LeafRow";

/** A Group header + its indented children. */
const GroupNode = ({
  node,
  path,
  currentPath,
  loopCounters,
  currentIdx,
  instancesByPath,
  activeRef,
  actualTimeMap,
  currentPhaseElapsedSec,
  depth,
  activeSetIdx,
}) => {
  const currentIter = loopCounters?.[node.id] ?? 0;
  const totalIters = node.value ?? 1;
  const showCounter = totalIters > 1;
  const label = formatLabel(node.label) || "Group";

  // Determine display count: ancestor groups show current iteration,
  // completed groups show their final count, unstarted groups show 0
  const hasCounter =
    node.id != null && loopCounters != null && node.id in loopCounters;
  const isAncestorOfCurrent =
    currentPath &&
    path.length < currentPath.length &&
    path.every((seg, i) => currentPath[i] === seg);
  const displayCount = isAncestorOfCurrent
    ? currentIter + 1 // Active: 1-based current iteration
    : hasCounter
      ? currentIter + 1 // Completed: counter is value-1, so +1 = total
      : 0; // Not entered yet

  // Per-round duration breakdown (parity with SessionPlanDisplay v0.48.0).
  // Compute once here so the dense outline shows both progress and pace.
  const groupTotal = useTotalDuration(node);
  const roundSummary = useGroupRoundSummary(node);
  const groupUnit = String(node.unit || "").toLowerCase();
  const showRoundBreakdown =
    (groupUnit === "rounds" || groupUnit === "sets") &&
    totalIters > 1 &&
    groupTotal > 0;
  // Round one's real duration, not `total / rounds` — see issue #73 and the
  // matching comment in SessionPlanDisplay. `~` marks a block whose rounds
  // differ (a set ramp, or the stripped final rest).
  const hasRoundSummary = showRoundBreakdown && roundSummary.rounds > 0;
  const perRoundCompact = showRoundBreakdown
    ? formatDurationCompact(
        hasRoundSummary
          ? roundSummary.roundDurations[0]
          : groupTotal / totalIters,
      )
    : null;
  const perRoundApprox = hasRoundSummary && roundSummary.roundsVary;

  // Set index governing this group's descendants — the same rule the compiler
  // uses to resolve a `setScheme` (nearest multi-round Group's iteration, see
  // resolveSetScheme). Only meaningful while this group holds the current
  // phase; otherwise the leaves render the whole ramp rather than pretending a
  // set is live. A single-iteration group is transparent and passes through.
  const childSetIdx =
    totalIters > 1
      ? isAncestorOfCurrent
        ? currentIter
        : null
      : activeSetIdx;

  return (
    <div className="space-y-0.5">
      {/* Group header row */}
      <div className="flex items-center gap-1.5 py-0.5 px-1 text-xs text-muted-foreground">
        <Repeat className="w-3 h-3 shrink-0" />
        <span className="font-medium truncate">{label}</span>
        {showCounter && (
          <span className="ml-auto whitespace-nowrap tabular-nums text-[10px] font-bold text-muted-foreground/70">
            {currentPath ? `${displayCount}/${totalIters}` : `${totalIters}×`}
            {perRoundCompact && (
              <span className="ml-1 font-medium opacity-80">
                {perRoundApprox ? "~" : ""}
                {perRoundCompact}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Children with indent thread */}
      {node.children && node.children.length > 0 && (
        <div className="pl-3 ml-1.5 border-l border-border/30 space-y-0.5">
          {node.children.map((child, idx) => {
            const childPath = [...path, "children", idx];
            return (
              <OutlineNode
                key={(child.id || child.label || "c") + "-" + idx}
                node={child}
                path={childPath}
                currentPath={currentPath}
                loopCounters={loopCounters}
                currentIdx={currentIdx}
                instancesByPath={instancesByPath}
                activeRef={activeRef}
                actualTimeMap={actualTimeMap}
                currentPhaseElapsedSec={currentPhaseElapsedSec}
                depth={depth + 1}
                activeSetIdx={childSetIdx}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Recursive dispatcher — renders either a GroupNode or a LeafRow. */
const OutlineNode = ({
  node,
  path,
  currentPath,
  loopCounters,
  currentIdx,
  instancesByPath,
  activeRef,
  actualTimeMap,
  currentPhaseElapsedSec,
  depth,
  activeSetIdx,
}) => {
  if (!node) return null;

  // Hide short intra-block Transition/Setup nodes from the at-rest summary.
  // The active coach (WorkoutCoachView's main phase panel) and the compiled
  // queue still see them — this filter is purely visual. See
  // isHiddenInSummary in planVisuals.js for the threshold rationale.
  if (isHiddenInSummary(node)) return null;

  const hasChildren = node.children && node.children.length > 0;

  if (hasChildren) {
    return (
      <GroupNode
        node={node}
        path={path}
        currentPath={currentPath}
        loopCounters={loopCounters}
        currentIdx={currentIdx}
        instancesByPath={instancesByPath}
        activeRef={activeRef}
        actualTimeMap={actualTimeMap}
        currentPhaseElapsedSec={currentPhaseElapsedSec}
        depth={depth}
        activeSetIdx={activeSetIdx}
      />
    );
  }

  const { status, completions, total } = getNodeStatus(
    path,
    currentIdx,
    instancesByPath,
  );
  const actualStats = node.id && actualTimeMap?.[node.id];
  return (
    <LeafRow
      ref={status === "current" ? activeRef : undefined}
      node={node}
      status={status}
      completions={completions}
      total={total}
      actualStats={actualStats}
      currentPhaseElapsedSec={currentPhaseElapsedSec}
      activeSetIdx={activeSetIdx}
    />
  );
};

// ---------------------------------------------------------------------------
// Standalone timer outline (Quick Sessions)
// ---------------------------------------------------------------------------

const StandaloneOutline = ({ timer }) => {
  const {
    mode,
    config,
    phase,
    currentRound,
    totalRounds,
    currentSet,
    totalSets,
    plan,
    phaseLog,
  } = timer;
  const preset = TIMER_PRESETS[mode];
  const macroLabels = getTimerMacroLabels(mode);
  const isInterval = mode === "tabata" || mode === "hiit";
  const phaseNodes = useMemo(
    () => getStandalonePhaseNodes(plan, mode),
    [plan, mode],
  );
  const phaseStatsMap = useMemo(
    () => buildPhaseStatsMap(phaseLog || []),
    [phaseLog],
  );

  // Simple modes with no phase breakdown
  if (mode === "stopwatch" || mode === "forTime" || mode === "amrap") {
    return (
      <div className="space-y-0.5">
        {totalRounds > 0 && (
          <div className="flex items-center gap-1.5 py-0.5 px-1 text-xs text-muted-foreground">
            <Repeat className="w-3 h-3 shrink-0" />
            <span className="font-medium">{preset?.label ?? mode}</span>
            <span className="ml-auto tabular-nums text-[10px] font-bold text-muted-foreground/70">
              Round {currentRound}/{totalRounds}
            </span>
          </div>
        )}
        {!totalRounds && (
          <div className="text-xs text-muted-foreground px-1">
            {preset?.label ?? mode}
          </div>
        )}
      </div>
    );
  }

  // Interval-based modes (tabata, hiit, emom, countdown, circuit)
  const phases = [];
  if (isInterval) {
    phases.push({
      label: "Work",
      type: "Work",
      value: config?.work,
      unit: "s",
      phase: "work",
      nodeId: phaseNodes.work?.id,
    });
    phases.push({
      label: "Rest",
      type: "Rest",
      value: config?.rest,
      unit: "s",
      phase: "rest",
      nodeId: phaseNodes.rest?.id,
    });
  } else if (mode === "emom") {
    phases.push({
      label: "Interval",
      type: "Work",
      value: config?.duration,
      unit: "s",
      phase: "work",
      nodeId: phaseNodes.work?.id,
    });
  } else if (mode === "countdown") {
    phases.push({
      label: "Countdown",
      type: "Work",
      value: config?.duration,
      unit: "s",
      phase: "countdown",
      nodeId: phaseNodes.work?.id,
    });
  } else if (mode === "stopwatch" || mode === "forTime") {
    phases.push({
      label: preset?.label ?? mode,
      type: "Work",
      value: 0,
      unit: "s",
      phase: "counting",
      nodeId: phaseNodes.work?.id,
    });
  }

  return (
    <div className="space-y-0.5">
      {/* Set level */}
      {totalSets > 1 && (
        <div className="flex items-center gap-1.5 py-0.5 px-1 text-xs text-muted-foreground">
          <Repeat className="w-3 h-3 shrink-0" />
          <span className="font-medium">Set</span>
          <span className="ml-auto tabular-nums text-[10px] font-bold text-muted-foreground/70">
            {currentSet}/{totalSets}
          </span>
        </div>
      )}

      {/* Round level */}
      <div
        className={cn(totalSets > 1 && "pl-3 ml-1.5 border-l border-border/30")}
      >
        {totalRounds > 0 && (
          <div className="flex items-center gap-1.5 py-0.5 px-1 text-xs text-muted-foreground">
            <Repeat className="w-3 h-3 shrink-0" />
            <span className="font-medium">{macroLabels.title}</span>
            <span className="ml-auto tabular-nums text-[10px] font-bold text-muted-foreground/70">
              {currentRound}/{totalRounds}
            </span>
          </div>
        )}

        {/* Phase rows */}
        <div
          className={cn(
            totalRounds > 0 && "pl-3 ml-1.5 border-l border-border/30",
            "space-y-0.5",
          )}
        >
          {phases.map((p) => (
            <div
              key={p.phase}
              className={cn(
                "flex items-center justify-between py-1 px-2 rounded text-sm transition-colors",
                phase === p.phase && "bg-primary/10",
                phase !== p.phase && "opacity-60",
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={cn(
                    "w-2 h-2 rounded-full shrink-0",
                    getDotClass(p.type),
                  )}
                />
                <span className="text-white/80">{p.label}</span>
              </div>
              <span className="text-xs text-white/50 whitespace-nowrap ml-2">
                {formatExVal(p.value, p.unit)}
              </span>
              {formatPhaseSummary(phaseStatsMap[p.nodeId]) && (
                <span className="text-[10px] text-white/30 whitespace-nowrap ml-2">
                  [{formatPhaseSummary(phaseStatsMap[p.nodeId])}]
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Set Rest */}
        {totalSets > 1 && config?.setRest > 0 && (
          <div
            className={cn(
              "flex items-center justify-between py-1 px-2 rounded text-sm transition-colors mt-0.5",
              phase === "setRest" && "bg-primary/10",
              phase !== "setRest" && "opacity-60",
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={cn(
                  "w-2 h-2 rounded-full shrink-0",
                  getDotClass("SetRest"),
                )}
              />
              <span className="text-white/80">Set rest</span>
            </div>
            <span className="text-xs text-white/50 whitespace-nowrap ml-2">
              {formatExVal(config.setRest, "s")}
            </span>
            {formatPhaseSummary(phaseStatsMap[phaseNodes.setRest?.id]) && (
              <span className="text-[10px] text-white/30 whitespace-nowrap ml-2">
                [{formatPhaseSummary(phaseStatsMap[phaseNodes.setRest?.id])}]
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SessionPlanOutline = ({
  plan,
  currentPath,
  loopCounters,
  phaseLog,
  currentPhaseElapsedSec,
  standaloneTimer,
  // When true (e.g. on the Coach setup screen between blocks), narrow the
  // plan-tree rendering to just the next top-level block after the current
  // path. Past and current top-level children are hidden so the focus is on
  // what's about to start, not the completed history.
  upcomingOnly = false,
  maxHeight = "max-h-[40vh]",
  className,
  // Shows an Edit link beside the heading (paused Coach, #175).
  onEdit,
}) => {
  const activeRef = useRef(null);

  // Fully expand the plan into a visit order once per plan change — iteration
  // counters shift as the session runs but the tree structure doesn't, so we
  // don't want to rebuild on every tick.
  const visitOrder = useMemo(() => (plan ? buildVisitOrder(plan) : []), [plan]);

  const instancesByPath = useMemo(
    () => buildInstancesByPath(visitOrder),
    [visitOrder],
  );

  const currentIdx = useMemo(
    () => findCurrentIdx(visitOrder, currentPath, loopCounters),
    [visitOrder, currentPath, loopCounters],
  );

  // Build actual-time lookup from phase log
  const actualTimeMap = useMemo(
    () => buildPhaseStatsMap(phaseLog || []),
    [phaseLog],
  );

  // Auto-scroll to the current exercise on path change — within the outline's
  // own list only. scrollIntoView scrolls every scrollable ancestor too, which
  // dragged the whole Coach screen down on pause and pushed the exercise name
  // and target off the top.
  useEffect(() => {
    const item = activeRef.current;
    if (!item) return;
    let list = item.parentElement;
    while (list && !/(auto|scroll)/.test(getComputedStyle(list).overflowY)) {
      list = list.parentElement;
    }
    if (!list) return;
    const lr = list.getBoundingClientRect();
    const ir = item.getBoundingClientRect();
    let delta = 0;
    if (ir.top < lr.top) delta = ir.top - lr.top - 8;
    else if (ir.bottom > lr.bottom) delta = ir.bottom - lr.bottom + 8;
    if (delta !== 0) {
      list.scrollTo?.({ top: list.scrollTop + delta, behavior: "smooth" });
    }
  }, [currentPath]);

  // Standalone timer mode
  if (standaloneTimer) {
    return (
      <div
        className={cn(
          "animate-in fade-in slide-in-from-bottom-2 duration-200",
          className,
        )}
      >
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 text-left">
          Overview
        </h3>
        <div className={cn("overflow-y-auto pr-1", maxHeight)}>
          <StandaloneOutline timer={standaloneTimer} />
        </div>
      </div>
    );
  }

  // Plan tree mode
  if (!plan?.children || plan.children.length === 0) return null;

  // Narrow to the next top-level child when caller asks for an "upcoming only"
  // view. Inter-block Setup transitions are top-level siblings of the Group
  // blocks, so the upcoming block is the first non-hidden child at an index
  // strictly greater than the current top-level position.
  const renderEntries = (() => {
    const all = plan.children.map((child, idx) => ({ child, idx }));
    if (!upcomingOnly) return all;
    if (!currentPath || currentPath[0] !== "children") return all;
    const topIdx = currentPath[1];
    if (typeof topIdx !== "number") return all;
    const next = all
      .filter((e) => e.idx > topIdx)
      .find((e) => !isHiddenInSummary(e.child));
    return next ? [next] : [];
  })();

  if (renderEntries.length === 0) return null;

  const heading = upcomingOnly
    ? "Coming up"
    : currentPath
      ? "Session plan"
      : "Exercises";

  return (
    <div
      className={cn(
        "animate-in fade-in slide-in-from-bottom-2 duration-200",
        className,
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-left">
          {heading}
        </h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="-my-2 flex items-center gap-1 rounded-md px-2 py-2 text-xs font-medium text-primary hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span>Edit</span>
          </button>
        )}
      </div>
      <div className={cn("overflow-y-auto space-y-0.5 pr-1", maxHeight)}>
        {renderEntries.map(({ child, idx }) => (
          <OutlineNode
            key={(child.id || child.label || "node") + "-" + idx}
            node={child}
            path={["children", idx]}
            currentPath={currentPath}
            loopCounters={loopCounters}
            currentIdx={currentIdx}
            instancesByPath={instancesByPath}
            activeRef={activeRef}
            actualTimeMap={actualTimeMap}
            currentPhaseElapsedSec={currentPhaseElapsedSec}
            depth={0}
          />
        ))}
      </div>
    </div>
  );
};

export default SessionPlanOutline;
